import { appConfig } from "@/constants";
import { notifyError } from "@/utils/monitoring";
import { trackLegacyEvent } from "@analytics";
import { Button, Stack } from "@biom3/react";
import { useConnection } from "@hooks/connection";
import { earnGem } from "@imx-tokens-ts/gems-game/data-access";
import type { WalletGems } from "@imx-tokens-ts/gems-game/data-access";
import { useCallback, useEffect, useState } from "react";
import { SuccessButton } from "../../SuccessButton/SuccessButton";
import { EOAWalletDisconnect } from "./EOAWalletDisconnect";

// This is a magical number that is sync'd to the Rive animation so
// it switches to Claimed as the numbers stop.
const TIME_TIL_SPINNING_STOPS = 2200;

interface GetYourGemProps {
  onGemEarned: (walletGems: WalletGems) => void;
  walletGems: WalletGems;
  hasJustEarned: boolean;
  disconnect: () => Promise<void>;
  isPassportWallet: boolean;
}

const IsClaimingButton = () => (
  <Button
    size="large"
    testId="claim-gems__confirm-btn"
    variant="tertiary"
    sx={{
      minWidth: "220px",
    }}
  >
    Confirm in wallet
    <Button.Icon icon="Loading" />
  </Button>
);

const RevealGemsButton = () => (
  <Button
    size="large"
    testId="claim-gems__confirm-btn"
    variant="primary"
    sx={{
      minWidth: "220px",
    }}
  >
    Get Ready
    <Button.Icon icon="Loading" />
  </Button>
);

const ClaimedButton = () => {
  return (
    <SuccessButton
      testId="claimed__daily-claimed"
      label="Gems Claimed"
      sx={{
        minHeight: "64px",
        minWidth: "220px",
      }}
    />
  );
};

export function GemsClaimCta({
  onGemEarned,
  walletGems,
  hasJustEarned,
  disconnect,
  isPassportWallet,
}: GetYourGemProps) {
  const { provider, walletAddress } = useConnection();
  const [isClaiming, setIsClaiming] = useState(false);
  const [isGettingReady, setIsGettingReady] = useState(false);

  useEffect(() => {
    if (hasJustEarned) {
      setIsGettingReady(true);
      const timer = setTimeout(() => {
        setIsGettingReady(false);
      }, TIME_TIL_SPINNING_STOPS);
      return () => clearTimeout(timer);
    }
  }, [hasJustEarned]);

  const getYourGem = useCallback(async () => {
    if (!provider) throw new Error("Provider is not connected");
    if (!walletAddress) throw new Error("Wallet address is not connected");
    try {
      setIsClaiming(true);
      trackLegacyEvent({
        screen: "Claim",
        action: "Pressed",
        control: "GetYourGem",
        controlType: "Button",
        userJourney: "Claim",
        extras: {
          variableGemsEnabled: true,
          walletAddress,
        },
      });
      await earnGem(
        provider,
        walletAddress,
        appConfig.GEMS_GAME_CONTRACT_ADDRESS,
      );
      onGemEarned(walletGems);
      setIsClaiming(false);
    } catch (e: unknown) {
      setIsClaiming(false);
      const EOA_CONNECTION_REJECTED_CODE = 4001;
      const PASSPORT_TRANSACTION_REJECTED = -32003;
      if (
        e instanceof Error &&
        (!("code" in e) ||
          (e.code !== EOA_CONNECTION_REJECTED_CODE &&
            e.code !== PASSPORT_TRANSACTION_REJECTED))
      ) {
        notifyError(e, "getYourGem");
      }
    }
  }, [onGemEarned, provider, walletAddress, walletGems]);

  if (isClaiming) {
    return <IsClaimingButton />;
  }

  if (isGettingReady) {
    return <RevealGemsButton />;
  }

  if (hasJustEarned) {
    return <ClaimedButton />;
  }

  return (
    <Stack direction={["column"]}>
      {!isPassportWallet && walletAddress ? (
        <EOAWalletDisconnect
          walletAddress={walletAddress}
          disconnect={disconnect}
        />
      ) : null}
      <Button
        size="large"
        testId="claim-gems__claim-btn"
        onClick={getYourGem}
        sx={{ minWidth: ["auto", "260px"] }}
      >
        Claim Gems
        <Button.Icon icon="ImxRewards" />
      </Button>
    </Stack>
  );
}
